/**
 * Générateur — Automatisme : coder des angles droits et des longueurs égales
 * Attendu ID : 1018 (À ARBITRER : numéro déduit du trou 1017 → 1019) | Niveau : 6ème | Domaine : GM
 * Rubrique : AUTOMATISME (programme 2025, cycle 3 p. 23, Étude de configurations
 * planes) — « Il sait coder des angles droits et des longueurs égales. »
 * Labels (cartes exerciseur) :
 *   - Coder la figure (QCM d'images : quatre fois le même dessin, seuls les codages changent)
 *   - Où placer le codage ? (QCM texte : le sommet du petit carré, les côtés des traits)
 *   - Le codage est-il juste ? (Vrai/Faux : une figure codée par un élève, une erreur ou aucune)
 *   - Chaque propriété avec son codage (relier : trois propriétés, trois figures)
 *
 * ── Cadrage (2026-09-18, sous-agent programme-maths, go prof) ──────────────
 * DEUX codages, pas trois : le petit carré de l'angle droit, les traits
 * identiques des longueurs égales. Les arcs d'angles égaux restent à 1015
 * (lecture). Sens PRODUCTION : l'information est donnée en MOTS (« rectangle
 * en B », « AB = AC », « (AB) et (BC) perpendiculaires », « l'angle ABC est
 * droit ») ou en MESURES écrites sur la figure ; l'élève produit ou choisit
 * le codage. 1015 fait le sens inverse (décoder).
 *
 * FIGURES À MAIN LEVÉE, VOLONTAIREMENT INEXACTES (arbitrage (2)) : l'angle
 * annoncé droit est tracé entre 70° et 78° ou entre 102° et 110°, les côtés
 * annoncés égaux diffèrent de 15 à 20 %. Le codage porte SEUL l'information ;
 * l'erreur principale visée est `code_ce_qu_il_voit` : coder l'allure du
 * dessin (le sommet qui PARAÎT droit, la paire qui PARAÎT égale) au lieu
 * des données. Chaque dessin embarque ce leurre visuel par construction :
 * un autre sommet à ±5° du droit, ou une autre paire de côtés égale à 3 %.
 *
 * Figures du cycle 3 : triangle rectangle, triangle isocèle, triangle
 * rectangle isocèle, rectangle (quatre angles droits, deux groupes de
 * longueurs), quadrilatère et pentagone quelconques à UN angle droit et/ou
 * UNE paire de côtés égaux (figures « anti-nom », arbitrage (4)). Toujours
 * tournées. Un ou deux traits, jamais trois.
 *
 * Notations [AB], (AB), angle ABC : employées et explicitées, jamais
 * interrogées. Crochets partout pour les côtés (arbitrage prof 1015).
 *
 * ── Erreurs ciblées (noms alignés sur 1015 + quatre neufs) ────────────────
 *   code_ce_qu_il_voit (PRINCIPALE) : coder l'allure du dessin ;
 *   autre_sommet, autre_paire, confusion_carre (carré à la place des traits),
 *   confusion_ticks (traits à la place du carré), codage_incomplet,
 *   codage_en_trop, traits_differents, tout_pareil (le rectangle codé comme
 *   un carré), refus_du_codage (« on ne peut pas savoir »).
 *
 * ── Arbitrages en mode auto, À ARBITRER ───────────────────────────────────
 * (1) arcs exclus ; (2) main levée fausse, code_ce_qu_il_voit principale ;
 * (3) nom de figure seul écarté (« rectangle » ⇒ codage : c'est du CM) ;
 * (4) quadrilatère / pentagone quelconques comme figures anti-nom ;
 * (5) mesures écrites sur la figure dans « Où placer ? » (sous-type côtés) ;
 * (6) pas de « placer des points » ; (7) V/F : quatre lignes qui sont des
 * FAITS vérifiables sur la figure (« il y a un petit carré en E », « le
 * codage indique que ED = EF », « la figure porte un codage que l'énoncé ne
 * donne pas », « la figure est correctement codée ») — les lignes « il manque
 * un codage » du cadrage étaient ambiguës quand le carré est au mauvais
 * sommet (manque ET trop) ; (8) un tirage sur trois sans erreur dans le V/F ;
 * (9) relier : trois figures numérotées sur une planche (le composant relier
 * n'accepte que du texte), lettres conservées ; (10) coup de pouce commun
 * aux quatre cartes, triangle UVW hors pool.
 */
import {
    randomChoice, randInt, shuffle, pickLettresSains,
    makeQcm, makeVraiFaux, makeRelier,
} from '../../../utils';

const ATTENDU_ID = 1018;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_CODER = 'Coder la figure';
const L_PLACER = 'Où placer le codage ?';
const L_JUSTE = 'Le codage est-il juste ?';
const L_RELIER = 'Chaque propriété avec son codage';

const garde = (condition, message) => {
    if (!condition) throw new Error(`gen_1018 : ${message}`);
};

const dataUrl = (svg) => `data:image/svg+xml,${encodeURIComponent(svg)}`;
const elImage = (url) => ({ version: 1, elements: [{ type: 'image', config: { imageUrl: url } }] });
const choixImage = (svg) => ({ type: 'image', config: { imageData: dataUrl(svg) } });

const BLEU = '#1E3A8A';
const ROUGE = '#dc2626';
const GRIS = '#374151';
const LETTRES_POOL = 'ABCDEFGHKLMNPRST'.split('');

// ─── Géométrie ────────────────────────────────────────────────────────────

const rad = (deg) => (deg * Math.PI) / 180;
const rot = (angleDeg) => {
    const a = rad(angleDeg);
    return ([x, y]) => [x * Math.cos(a) - y * Math.sin(a), x * Math.sin(a) + y * Math.cos(a)];
};
const dist = (P, Q) => Math.hypot(P[0] - Q[0], P[1] - Q[1]);
const unit = (P, Q) => {
    const d = dist(P, Q);
    return [(Q[0] - P[0]) / d, (Q[1] - P[1]) / d];
};
/** Angle intérieur (degrés) au sommet i d'un polygone. */
const angleEn = (pts, i) => {
    const n = pts.length;
    const u = unit(pts[i], pts[(i + n - 1) % n]);
    const v = unit(pts[i], pts[(i + 1) % n]);
    return (Math.acos(Math.max(-1, Math.min(1, u[0] * v[0] + u[1] * v[1]))) * 180) / Math.PI;
};
/** Longueur du côté k = [pts[k], pts[k+1]]. */
const cote = (pts, k) => dist(pts[k], pts[(k + 1) % pts.length]);
const convexe = (pts) => {
    const n = pts.length;
    let signe = 0;
    for (let i = 0; i < n; i++) {
        const A = pts[i]; const B = pts[(i + 1) % n]; const C = pts[(i + 2) % n];
        const z = (B[0] - A[0]) * (C[1] - B[1]) - (B[1] - A[1]) * (C[0] - B[0]);
        if (Math.abs(z) < 1e-6) return false;
        if (signe === 0) signe = Math.sign(z);
        else if (Math.sign(z) !== signe) return false;
    }
    return true;
};
/** Angle « presque droit » tracé à main levée : 70-78° ou 102-110°. */
const presqueDroit = () => 90 + randomChoice([1, -1]) * randInt(12, 20);
/** Facteur d'inexactitude des longueurs annoncées égales : ×1,15 à ×1,20 (ou l'inverse). */
const inexact = () => (Math.random() < 0.5 ? 1 + randInt(15, 20) / 100 : 1 / (1 + randInt(15, 20) / 100));
/** Point à distance r de P dans la direction (degrés, repère écran y vers le bas). */
const depuis = (P, r, deg) => [P[0] + r * Math.cos(rad(deg)), P[1] - r * Math.sin(rad(deg))];

/**
 * Constructeurs de figures À MAIN LEVÉE. Chaque figure déclare ce qui est
 * ANNONCÉ (droitEn : indices des sommets ; egaux : groupes de côtés k) et ce
 * que le dessin FAIT VOIR (vuDroit : un autre sommet tracé à ±5° du droit ;
 * vuEgaux : une autre paire de côtés égale à 3 %) — le leurre visuel de
 * code_ce_qu_il_voit. Coordonnées locales, y vers le bas.
 */
const FIGURES = {
    /** Triangle « rectangle en 1 » : angle en 1 tracé presque droit, autre sommet le plus proche du droit possible. */
    triangle_rect: () => {
        for (let essai = 0; essai < 60; essai++) {
            const a = randInt(70, 95);
            const b = Math.round(a * (1.2 + Math.random() * 0.4));
            const phi = presqueDroit();
            const pts = [depuis([0, 0], a, 90), [0, 0], depuis([0, 0], b, 90 - phi)];
            if (!convexe(pts)) continue;
            return { pts, droitEn: [1], egaux: [], vuDroit: null, vuEgaux: null };
        }
        return { pts: [[0, -80], [0, 0], [110, 20]], droitEn: [1], egaux: [], vuDroit: null, vuEgaux: null };
    },
    /** Triangle « isocèle en 0 » (côtés 2 = [2,0] et 0 = [0,1] annoncés égaux) ; tracé isocèle en 2 (côtés 1 et 2 égaux). */
    triangle_iso: () => {
        for (let essai = 0; essai < 60; essai++) {
            const L = randInt(80, 105);
            const f = inexact();
            const c01 = L; const c20 = Math.round(L * f);          // annoncés égaux, tracés différents
            const c12 = c20 * (1 + randomChoice([1, -1]) * randInt(0, 3) / 100);   // paire VUE égale : [1,2] ≈ [2,0]
            if (c01 + c12 <= c20 + 4 || c01 + c20 <= c12 + 4 || c12 + c20 <= c01 + 4) continue;
            // B = pts[1] à l'origine, C = pts[2] sur l'axe, A = pts[0] par intersection de cercles
            const B = [0, 0]; const C = [c12, 0];
            const x = (c01 * c01 - c20 * c20 + c12 * c12) / (2 * c12);
            const y2 = c01 * c01 - x * x;
            if (y2 <= 0) continue;
            const A = [x, -Math.sqrt(y2)];
            const pts = [A, B, C];
            if (Math.min(angleEn(pts, 0), angleEn(pts, 1), angleEn(pts, 2)) < 28) continue;
            return { pts, droitEn: [], egaux: [[2, 0]], vuDroit: null, vuEgaux: [1, 2] };
        }
        return { pts: [[38, -88], [0, 0], [100, 0]], droitEn: [], egaux: [[2, 0]], vuDroit: null, vuEgaux: [1, 2] };
    },
    /** Triangle « rectangle en 1 et [1,0] = [1,2] » : angle presque droit, côtés de l'angle inégaux de 15-20 %. */
    triangle_rect_iso: () => {
        const a = randInt(75, 95);
        const b = Math.round(a * inexact());
        const phi = presqueDroit();
        const pts = [depuis([0, 0], a, 90), [0, 0], depuis([0, 0], b, 90 - phi)];
        return { pts, droitEn: [1], egaux: [[0, 1]], vuDroit: null, vuEgaux: null };
    },
    /** « Rectangle » à main levée : un vrai rectangle dont chaque sommet est décalé ; quatre angles annoncés droits tracés à 90° ± 3-18°, longs [1,2],[3,0] et courts [0,1],[2,3] inégaux de 5 à 20 % dans chaque groupe. */
    rectangle: () => {
        for (let essai = 0; essai < 200; essai++) {
            const h = randInt(55, 72);
            const w = Math.round(h * (1.45 + Math.random() * 0.4));
            const bouge = ([x, y]) => [x + randomChoice([1, -1]) * randInt(4, 10), y + randomChoice([1, -1]) * randInt(4, 10)];
            const pts = [[0, -h], [0, 0], [w, 0], [w, -h]].map(bouge);
            if (!convexe(pts)) continue;
            const angles = [0, 1, 2, 3].map((i) => angleEn(pts, i));
            if (angles.some((x) => Math.abs(x - 90) < 3 || Math.abs(x - 90) > 18)) continue;
            const long = [cote(pts, 1), cote(pts, 3)]; const court = [cote(pts, 0), cote(pts, 2)];
            if (Math.abs(long[0] - long[1]) < 0.05 * Math.max(...long) || Math.abs(court[0] - court[1]) < 0.05 * Math.max(...court)) continue;
            if (Math.abs(long[0] - long[1]) > 0.2 * Math.max(...long) || Math.abs(court[0] - court[1]) > 0.2 * Math.max(...court)) continue;
            if (Math.min(...long) < 1.25 * Math.max(...court)) continue;
            return { pts, droitEn: [0, 1, 2, 3], egaux: [[1, 3], [0, 2]], vuDroit: null, vuEgaux: null };
        }
        return { pts: [[14, -70], [0, 0], [104, 6], [126, -54]], droitEn: [0, 1, 2, 3], egaux: [[1, 3], [0, 2]], vuDroit: null, vuEgaux: null };
    },
    /** Quadrilatère « un angle droit en 1 » : tracé presque droit en 1, et un AUTRE sommet tracé à ±5° du droit (le leurre visuel). */
    quadri_un_droit: () => {
        for (let essai = 0; essai < 120; essai++) {
            const a = randInt(60, 85);
            const b = randInt(75, 105);
            const phi = presqueDroit();
            const B = [0, 0];
            const A = depuis(B, a, 90);
            const C = depuis(B, b, 90 - phi);
            const D = [A[0] + C[0] + randInt(-25, 25), A[1] + C[1] + randInt(-25, 25)];
            const pts = [A, B, C, D];
            if (!convexe(pts)) continue;
            const angles = [0, 1, 2, 3].map((i) => angleEn(pts, i));
            if (angles.some((x) => x < 40 || x > 140)) continue;
            const autres = [0, 2, 3].filter((i) => Math.abs(angles[i] - 90) <= 5);
            if (autres.length !== 1) continue;
            const vu = autres[0];
            if ([0, 2, 3].some((i) => i !== vu && Math.abs(angles[i] - 90) < 12)) continue;
            // aucune paire de côtés trop proche (le quadrilatère ne doit rien « dire » sur les longueurs)
            const cs = [0, 1, 2, 3].map((k) => cote(pts, k));
            let ok = true;
            for (let p = 0; p < 4 && ok; p++) for (let q = p + 1; q < 4; q++) if (Math.abs(cs[p] - cs[q]) < 0.08 * Math.max(cs[p], cs[q])) { ok = false; break; }
            if (!ok) continue;
            return { pts, droitEn: [1], egaux: [], vuDroit: vu, vuEgaux: null };
        }
        return { pts: [[0, -70], [0, 0], [96, 22], [64, -92]], droitEn: [1], egaux: [], vuDroit: 3, vuEgaux: null };
    },
    /** Pentagone « angle droit en 1, [2,3] = [3,4] » : angle presque droit en 1, côtés 2 et 3 inégaux, côtés 3 et 4 VUS égaux. */
    pentagone: () => {
        for (let essai = 0; essai < 150; essai++) {
            const a = randInt(60, 80);          // [1,0]
            const b = randInt(65, 90);          // [1,2]
            const phi = presqueDroit();
            const B = [0, 0];
            const A = depuis(B, a, 90);
            const C = depuis(B, b, 90 - phi);
            // E au-dessus de A, D « pointe » ; côté 3 = [D,E] et côté 4 = [E,A] tracés égaux, côté 2 = [C,D] inexact
            const e = randInt(45, 70);
            const E = depuis(A, e, randInt(20, 45));
            const dE = e * (1 + randomChoice([1, -1]) * randInt(0, 3) / 100);   // [D,E] ≈ [E,A]
            const dC = Math.round(dE * inexact());                                            // [C,D] annoncé = [D,E], tracé inexact
            // D : intersection des cercles (C, dC) et (E, dE), côté extérieur
            const dCE = dist(C, E);
            if (dCE >= dC + dE - 4 || dCE <= Math.abs(dC - dE) + 4) continue;
            const x = (dC * dC - dE * dE + dCE * dCE) / (2 * dCE);
            const h = Math.sqrt(Math.max(0, dC * dC - x * x));
            const u = unit(C, E); const nrm = [-u[1], u[0]];
            const M = [C[0] + x * u[0], C[1] + x * u[1]];
            const D1 = [M[0] + h * nrm[0], M[1] + h * nrm[1]];
            const D2 = [M[0] - h * nrm[0], M[1] - h * nrm[1]];
            const D = dist(D1, B) > dist(D2, B) ? D1 : D2;
            const pts = [A, B, C, D, E];
            if (!convexe(pts)) continue;
            const angles = [0, 1, 2, 3, 4].map((i) => angleEn(pts, i));
            if (angles.some((x) => x < 45 || x > 150)) continue;
            if ([0, 2, 3, 4].some((i) => Math.abs(angles[i] - 90) < 12)) continue;   // un seul angle « presque droit »
            // les autres paires de côtés ne doivent pas paraître égales (sauf la paire VUE [3],[4])
            const cs = [0, 1, 2, 3, 4].map((k) => cote(pts, k));
            let ok = true;
            for (let p = 0; p < 5 && ok; p++) for (let q = p + 1; q < 5; q++) {
                if ((p === 3 && q === 4)) continue;
                if (Math.abs(cs[p] - cs[q]) < 0.08 * Math.max(cs[p], cs[q])) { ok = false; break; }
            }
            if (!ok) continue;
            return { pts, droitEn: [1], egaux: [[2, 3]], vuDroit: null, vuEgaux: [3, 4] };
        }
        return { pts: [[0, -70], [0, 0], [82, 18], [96, -50], [44, -104]], droitEn: [1], egaux: [[2, 3]], vuDroit: null, vuEgaux: [3, 4] };
    },
};

/** Rotation quelconque + recadrage (étiquettes et mesures comprises). */
const poserFigure = (pts, marge = 34) => {
    const r = rot(randInt(0, 359));
    let tourne = pts.map(r);
    const dx = marge - Math.min(...tourne.map((p) => p[0]));
    const dy = marge - Math.min(...tourne.map((p) => p[1]));
    tourne = tourne.map(([x, y]) => [x + dx, y + dy]);
    const W = Math.ceil(Math.max(...tourne.map((p) => p[0])) + marge);
    const H = Math.ceil(Math.max(...tourne.map((p) => p[1])) + marge);
    return { pts: tourne, W, H };
};

// ─── Les codages dessinés ─────────────────────────────────────────────────

const svgCarre = (S, U, V, sommet) => {
    const u = unit(S, U);
    const v = unit(S, V);
    const s = 12;
    const p1 = [S[0] + s * u[0], S[1] + s * u[1]];
    const p2 = [S[0] + s * (u[0] + v[0]), S[1] + s * (u[1] + v[1])];
    const p3 = [S[0] + s * v[0], S[1] + s * v[1]];
    return `<path d="M ${p1[0].toFixed(1)} ${p1[1].toFixed(1)} L ${p2[0].toFixed(1)} ${p2[1].toFixed(1)} L ${p3[0].toFixed(1)} ${p3[1].toFixed(1)}" fill="none" stroke="${ROUGE}" stroke-width="1.8" data-code="carre" data-sommet="${sommet}"/>`;
};

const svgTicks = (P, Q, n, nom) => {
    const u = unit(P, Q);
    const nx = -u[1];
    const ny = u[0];
    const mx = (P[0] + Q[0]) / 2;
    const my = (P[1] + Q[1]) / 2;
    const out = [];
    for (let i = 0; i < n; i++) {
        const off = (i - (n - 1) / 2) * 5;
        const cx = mx + off * u[0];
        const cy = my + off * u[1];
        out.push(`<line x1="${(cx - 6 * nx).toFixed(1)}" y1="${(cy - 6 * ny).toFixed(1)}" x2="${(cx + 6 * nx).toFixed(1)}" y2="${(cy + 6 * ny).toFixed(1)}" stroke="${ROUGE}" stroke-width="2" data-code="tick" data-cote="${nom}" data-n="${n}"/>`);
    }
    return out.join('\n  ');
};

/** Nom canonique d'un côté : les deux lettres dans l'ordre alphabétique. */
const nomCote = (L, i, j) => [L[i], L[j]].sort().join('');

/**
 * Le CONTENU d'une figure posée (polygone, codages, lettres, mesures), sans
 * balise <svg> : réutilisé seul (une image) ou sur une planche.
 * `codes` (en INDICES) : { carres: [i], ticks: [{ cotes: [k], n }] } où un
 * côté k est [pts[k], pts[k+1]]. `mesures` : { k: '5 cm' } écrit à l'extérieur
 * du côté k.
 */
const corpsFigure = (pose, L, codes, mesures) => {
    const { pts } = pose;
    const n = pts.length;
    const centre = pts.reduce(([sx, sy], [x, y]) => [sx + x / n, sy + y / n], [0, 0]);
    const p = [];
    p.push(`  <polygon points="${pts.map((pt) => `${pt[0].toFixed(1)},${pt[1].toFixed(1)}`).join(' ')}" fill="#eff6ff" stroke="${BLEU}" stroke-width="2.2"/>`);
    (codes.carres || []).forEach((i) => {
        p.push('  ' + svgCarre(pts[i], pts[(i + n - 1) % n], pts[(i + 1) % n], L[i]));
    });
    (codes.ticks || []).forEach((g) => {
        g.cotes.forEach((k) => {
            p.push('  ' + svgTicks(pts[k], pts[(k + 1) % n], g.n, nomCote(L, k, (k + 1) % n)));
        });
    });
    Object.entries(mesures || {}).forEach(([k, texte]) => {
        const i = Number(k); const P = pts[i]; const Q = pts[(i + 1) % n];
        const M = [(P[0] + Q[0]) / 2, (P[1] + Q[1]) / 2];
        const ux = M[0] - centre[0]; const uy = M[1] - centre[1]; const d = Math.hypot(ux, uy) || 1;
        const tx = M[0] + (ux / d) * 16; const ty = M[1] + (uy / d) * 16;
        p.push(`  <text x="${tx.toFixed(1)}" y="${(ty + 4.5).toFixed(1)}" font-size="13" font-weight="600" fill="${GRIS}" text-anchor="middle" font-family="system-ui,sans-serif" data-mesure="${nomCote(L, i, (i + 1) % n)}" data-valeur="${texte}">${texte}</text>`);
    });
    pts.forEach((pt, i) => {
        const ux = pt[0] - centre[0];
        const uy = pt[1] - centre[1];
        const d = Math.hypot(ux, uy);
        const lx = pt[0] + (ux / d) * 17;
        const ly = pt[1] + (uy / d) * 17;
        p.push(`  <text x="${lx.toFixed(1)}" y="${(ly + 5).toFixed(1)}" font-size="15" font-weight="700" fill="#111827" text-anchor="middle" font-family="system-ui,sans-serif" data-sommet="${L[i]}" data-i="${i}" data-x="${pt[0].toFixed(1)}" data-y="${pt[1].toFixed(1)}">${L[i]}</text>`);
    });
    return p.join('\n');
};

const svgFigure = (pose, L, codes, mesures) => {
    const { W, H } = pose;
    return [
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${Math.min(300, W)}" role="img"`,
        `  aria-label="Une figure ${L.join('')} tracée à main levée${Object.keys(codes).length ? ' avec des codages' : ''}.">`,
        corpsFigure(pose, L, codes, mesures),
        '</svg>',
    ].join('\n');
};

/** Planche : la MÊME figure posée, répétée avec des codages différents, numérotée. */
const svgPlanche = (pose, L, listeCodes) => {
    const { W, H } = pose;
    const BANDEAU = 24;
    const GAP = 10;
    const p = [];
    const total = listeCodes.length * (W + GAP) - GAP;
    p.push(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${total} ${H + BANDEAU}" width="${Math.min(560, total)}" role="img"`);
    p.push(`  aria-label="Trois fois la même figure ${L.join('')} à main levée, numérotées 1 à 3, codées différemment.">`);
    listeCodes.forEach((codes, k) => {
        const x = k * (W + GAP);
        // Un groupe translaté, pas un <svg> imbriqué : le nettoyeur de l'app (sanitizeSvg) retire
        // width/height de TOUTES les balises <svg>, un SVG imbriqué s'étalait alors sur toute la planche.
        p.push(`  <g transform="translate(${x} 0)" data-figure="${k + 1}">`);
        p.push(corpsFigure(pose, L, codes));
        p.push('  </g>');
        p.push(`  <text x="${x + W / 2}" y="${H + BANDEAU - 6}" font-size="15" font-weight="700" fill="${BLEU}" text-anchor="middle" font-family="system-ui,sans-serif">Figure ${k + 1}</text>`);
    });
    p.push('</svg>');
    return p.join('\n');
};

// ─── Vocabulaire des énoncés ──────────────────────────────────────────────

const seg = (L, i, j) => `[${[L[i], L[j]].sort().join('')}]`;
const egal = (L, k1, k2, pivot) => {
    // deux côtés k1, k2 écrits avec leur sommet commun `pivot` en tête quand il existe
    const n = L.length;
    const bouts = (k) => [k, (k + 1) % n];
    const [a1, b1] = bouts(k1); const [a2, b2] = bouts(k2);
    if (pivot !== undefined) {
        const autre = (k) => bouts(k).find((i) => i !== pivot);
        return `${L[pivot]}${L[autre(k1)]} = ${L[pivot]}${L[autre(k2)]}`;
    }
    return `${L[a1]}${L[b1]} = ${L[a2]}${L[b2]}`;
};
const nomFigure = (L) => L.join('');
const NOM_POLY = { 3: 'Le triangle', 4: 'Le quadrilatère', 5: 'Le pentagone' };

/** Codage attendu d'une figure : { carres: droitEn, ticks: groupes de traits (1 puis 2). } */
const codageAttendu = (fig) => ({
    carres: [...fig.droitEn],
    ticks: fig.egaux.map((cotes, g) => ({ cotes: [...cotes], n: g + 1 })),
});

/** Sommet d'un triangle/quadrilatère qui n'est pas dans `exclus`, au hasard. */
const autreSommet = (n, exclus) => randomChoice([...Array(n).keys()].filter((i) => !exclus.includes(i)));
/** Le sommet que le dessin fait voir droit, sinon un autre sommet au hasard : { i, code }. */
const sommetVu = (fig) => (fig.vuDroit !== null
    ? { i: fig.vuDroit, code: 'code_ce_qu_il_voit' }
    : { i: autreSommet(fig.pts.length, fig.droitEn), code: 'autre_sommet' });
/** La paire que le dessin fait voir égale, sinon une autre paire : { cotes, code }. */
const paireVue = (fig) => {
    if (fig.vuEgaux) return { cotes: [...fig.vuEgaux], code: 'code_ce_qu_il_voit' };
    const n = fig.pts.length;
    const annoncees = fig.egaux.map((c) => c.join('-'));
    const toutes = [];
    for (let p = 0; p < n; p++) for (let q = p + 1; q < n; q++) if (!annoncees.includes(`${p}-${q}`)) toutes.push([p, q]);
    return { cotes: randomChoice(toutes), code: 'autre_paire' };
};

// ─── Assemblage ───────────────────────────────────────────────────────────

const finirQcm = (bonne, dis, texte, sousType, label, elements) => {
    const payload = makeQcm(
        [bonne, ...dis.map((c) => c.valeur)],
        bonne,
        texte,
        { ...opts(label), ...(elements ? { elements_additionnels: elements } : {}) },
        false,
    );
    payload.contenu = { ...payload.contenu, sousType };
    payload._meta.params = { sousType, distracteurs: dis.map((c) => ({ code: c.code, ...(c.detail ? { detail: c.detail } : {}) })) };
    return payload;
};

// ═══════════════════════════════════════════════════════════════════════════
// Carte 1 — Coder la figure (QCM d'images)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Quatre fois le MÊME dessin (même pose), seuls les codages changent. Une
 * famille d'erreur par distracteur, jamais deux de la même.
 */
const genCoderQcm = () => {
    const sousType = randomChoice(['angle_triangle', 'angle_quadri', 'longueurs', 'rect_iso', 'rectangle', 'pentagone']);
    const forme = {
        angle_triangle: 'triangle_rect', angle_quadri: 'quadri_un_droit', longueurs: 'triangle_iso',
        rect_iso: 'triangle_rect_iso', rectangle: 'rectangle', pentagone: 'pentagone',
    }[sousType];
    const fig = FIGURES[forme]();
    const n = fig.pts.length;
    const L = pickLettresSains(LETTRES_POOL, n);
    const pose = poserFigure(fig.pts);
    const bon = codageAttendu(fig);
    const nom = nomFigure(L);
    let enonce;
    let dis;   // [{ codes, code }]
    if (sousType === 'angle_triangle' || sousType === 'angle_quadri') {
        const S = L[1];
        enonce = n === 3
            ? `Le triangle ${nom} est <b>rectangle en ${S}</b>.`
            : `Le quadrilatère ${nom} a <b>un angle droit en ${S}</b>, et un seul.`;
        const vu = sommetVu(fig);
        const troisieme = randomChoice([
            { codes: { carres: [1], ticks: [{ cotes: [0, 1], n: 1 }] }, code: 'codage_en_trop', detail: 'les côtés de l\'angle droit codés égaux sans que rien ne le dise' },
            { codes: { carres: [1, vu.i] }, code: 'codage_en_trop', detail: 'un second petit carré au sommet qui paraît droit' },
        ]);
        dis = [
            { codes: { carres: [vu.i] }, code: vu.code },
            { codes: { ticks: [{ cotes: [0, 1], n: 1 }] }, code: 'confusion_ticks' },
            troisieme,
        ];
    } else if (sousType === 'longueurs') {
        enonce = `Dans le triangle ${nom}, <b>${egal(L, 0, 2, 0)}</b>.`;
        const vu = paireVue(fig);
        dis = [
            { codes: { ticks: [{ cotes: vu.cotes, n: 1 }] }, code: vu.code },
            { codes: { carres: [0] }, code: 'confusion_carre' },
            randomChoice([
                { codes: { ticks: [{ cotes: [randomChoice([0, 2])], n: 1 }] }, code: 'codage_incomplet' },
                { codes: { ticks: [{ cotes: [0], n: 1 }, { cotes: [2], n: 2 }] }, code: 'traits_differents' },
            ]),
        ];
    } else if (sousType === 'rect_iso') {
        enonce = `Le triangle ${nom} est <b>rectangle en ${L[1]}</b> et <b>${egal(L, 0, 1, 1)}</b>.`;
        dis = [
            randomChoice([
                { codes: { carres: [1] }, code: 'codage_incomplet', detail: 'les traits manquent' },
                { codes: { ticks: [{ cotes: [0, 1], n: 1 }] }, code: 'codage_incomplet', detail: 'le petit carré manque' },
            ]),
            { codes: { carres: [autreSommet(3, [1])], ticks: [{ cotes: [0, 1], n: 1 }] }, code: 'autre_sommet' },
            randomChoice([
                { codes: { carres: [1], ticks: [{ cotes: [0], n: 1 }, { cotes: [1], n: 2 }] }, code: 'traits_differents' },
                { codes: { carres: [1], ticks: [{ cotes: [1, 2], n: 1 }] }, code: 'autre_paire' },
            ]),
        ];
    } else if (sousType === 'rectangle') {
        enonce = `${nom} a <b>quatre angles droits</b>, <b>${egal(L, 1, 3)}</b> et <b>${egal(L, 0, 2)}</b> (mais ${L[0]}${L[1]} et ${L[1]}${L[2]} n'ont pas la même longueur).`;
        dis = [
            { codes: { carres: [0, 1, 2, 3], ticks: [{ cotes: [0, 1, 2, 3], n: 1 }] }, code: 'tout_pareil' },
            { codes: { carres: [randomChoice([0, 1, 2, 3])], ticks: bon.ticks }, code: 'codage_incomplet', detail: 'un seul angle droit codé sur quatre' },
            { codes: { carres: [0, 1, 2, 3], ticks: [{ cotes: [0, 1], n: 1 }, { cotes: [2, 3], n: 2 }] }, code: 'autre_paire', detail: 'des côtés voisins codés égaux' },
        ];
    } else {
        // pentagone : angle droit en 1, [2,3] = [3,4]
        enonce = `Le pentagone ${nom} a <b>un angle droit en ${L[1]}</b> et <b>${egal(L, 2, 3, 3)}</b>.`;
        const vu = paireVue(fig);
        const sv = sommetVu(fig);
        dis = [
            { codes: { carres: [1], ticks: [{ cotes: vu.cotes, n: 1 }] }, code: vu.code },
            { codes: { carres: [sv.i], ticks: bon.ticks }, code: sv.code === 'code_ce_qu_il_voit' ? 'code_ce_qu_il_voit' : 'autre_sommet' },
            randomChoice([
                { codes: { carres: [1, 3] }, code: 'confusion_carre', detail: 'un petit carré au sommet commun des côtés égaux' },
                { codes: { carres: [1], ticks: [{ cotes: [2], n: 1 }, { cotes: [3], n: 2 }] }, code: 'traits_differents' },
            ]),
        ];
    }
    garde(new Set(dis.map((d) => d.code)).size === 3, `familles de distracteurs distinctes (${sousType})`);
    const images = [svgFigure(pose, L, bon), ...dis.map((d) => svgFigure(pose, L, d.codes))];
    garde(new Set(images).size === 4, `quatre images distinctes (${sousType})`);
    const choix = images.map(choixImage);
    const texte = `<p>${enonce}</p><p><b>Quelle figure est correctement codée ?</b> (Les figures sont tracées à main levée : seul le codage compte.)</p>`;
    const payload = makeQcm(choix, choix[0], texte, opts(L_CODER), false);
    payload.contenu = { ...payload.contenu, sousType };
    payload._meta.params = { sousType, forme, distracteurs: dis.map((d) => ({ code: d.code, ...(d.detail ? { detail: d.detail } : {}) })) };
    return payload;
};

// ═══════════════════════════════════════════════════════════════════════════
// Carte 2 — Où placer le codage ? (QCM texte, figure NON codée)
// ═══════════════════════════════════════════════════════════════════════════

const genPlacerQcm = () => {
    const sousType = randomChoice(['sommet', 'sommet', 'cotes']);
    if (sousType === 'sommet') {
        const forme = randomChoice(['triangle_rect', 'quadri_un_droit', 'pentagone']);
        const fig = FIGURES[forme]();
        const n = fig.pts.length;
        const L = pickLettresSains(LETTRES_POOL, n);
        const pose = poserFigure(fig.pts);
        const svg = svgFigure(pose, L, {});
        const S = L[1]; const U = L[0]; const V = L[2];
        const formulation = randomChoice(['perpendiculaires', 'angle']);
        const donnee = formulation === 'perpendiculaires'
            ? `Les droites <b>(${U}${S})</b> et <b>(${S}${V})</b> sont <b>perpendiculaires</b>.`
            : `L'angle <b>$\\widehat{${U}${S}${V}}$</b> est <b>droit</b>.`;
        const vu = sommetVu(fig);
        const autres = [...Array(n).keys()].filter((i) => i !== 1 && i !== vu.i);
        const dis = [
            { valeur: L[vu.i], code: vu.code },
            { valeur: L[randomChoice(autres)], code: 'autre_sommet' },
            { valeur: 'on ne peut pas le savoir', code: 'refus_du_codage' },
        ];
        return finirQcm(S, dis,
            `<p>${NOM_POLY[n]} ${nomFigure(L)} est tracé à main levée, sans codage. ${donnee}</p>`
            + '<p><b>À quel sommet faut-il placer le petit carré de l\'angle droit ?</b></p><p>{{element:0}}</p>',
            `placer_sommet_${formulation}`, L_PLACER, elImage(dataUrl(svg)));
    }
    // côtés : triangle à main levée, mesures ÉCRITES sur la figure (arbitrage (5))
    const fig = FIGURES.triangle_iso();     // annoncés égaux : côtés 2 = [2,0] et 0 = [0,1] ; VUS égaux : 1 et 2
    const L = pickLettresSains(LETTRES_POOL, 3);
    const pose = poserFigure(fig.pts, 40);
    const m = randInt(4, 8);
    const autre = randomChoice([m - 2, m - 1, m + 1, m + 2].filter((x) => x >= 3 && x < 2 * m));
    const mesures = { 0: `${m} cm`, 2: `${m} cm`, 1: `${autre} cm` };
    const svg = svgFigure(pose, L, {}, mesures);
    const paire = (k1, k2) => `${seg(L, k1, (k1 + 1) % 3)} et ${seg(L, k2, (k2 + 1) % 3)}`;
    const dis = [
        { valeur: paire(1, 2), code: 'code_ce_qu_il_voit' },
        { valeur: paire(0, 1), code: 'autre_paire' },
        { valeur: 'on ne peut pas le savoir', code: 'refus_du_codage' },
    ];
    return finirQcm(paire(0, 2), dis,
        `<p>Le triangle ${nomFigure(L)} est tracé à main levée : ses mesures sont écrites sur la figure, mais le dessin n'est pas exact.</p>`
        + '<p><b>Sur quels côtés faut-il placer les mêmes petits traits ?</b></p><p>{{element:0}}</p>',
        'placer_cotes_mesures', L_PLACER, elImage(dataUrl(svg)));
};

// ═══════════════════════════════════════════════════════════════════════════
// Carte 3 — Le codage est-il juste ? (Vrai/Faux sur la figure d'un élève)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Une figure « rectangle en S et [S,U] = [S,V] » (triangle) ou « angle droit
 * en B et CD = DE » (pentagone), codée par un élève : exactement une erreur,
 * ou aucune (un tirage sur trois). Quatre lignes, quatre FAITS vérifiables.
 */
const genJusteVf = () => {
    const forme = randomChoice(['triangle_rect_iso', 'pentagone']);
    const fig = FIGURES[forme]();
    const n = fig.pts.length;
    const L = pickLettresSains(LETTRES_POOL, n);
    const pose = poserFigure(fig.pts);
    const bon = codageAttendu(fig);
    const iCarre = 1;
    const [k1, k2] = fig.egaux[0];                       // côtés annoncés égaux
    const pivot = forme === 'triangle_rect_iso' ? 1 : 3;  // leur sommet commun
    const tiers = [...Array(n).keys()].filter((k) => k !== k1 && k !== k2);   // les autres côtés
    const vuS = sommetVu(fig);
    const vuP = paireVue(fig);
    const erreurs = [
        { code: vuS.code === 'code_ce_qu_il_voit' ? 'code_ce_qu_il_voit' : 'autre_sommet', codes: { carres: [vuS.i], ticks: bon.ticks } },
        { code: vuP.code === 'code_ce_qu_il_voit' ? 'code_ce_qu_il_voit' : 'autre_paire', codes: { carres: [iCarre], ticks: [{ cotes: vuP.cotes, n: 1 }] } },
        { code: 'codage_incomplet', codes: randomChoice([{ carres: [iCarre] }, { ticks: bon.ticks }]) },
        { code: 'codage_en_trop', codes: randomChoice([
            { carres: [iCarre], ticks: [{ cotes: [k1, k2, randomChoice(tiers)], n: 1 }] },
            { carres: [iCarre, vuS.i], ticks: bon.ticks },
        ]) },
        { code: 'traits_differents', codes: { carres: [iCarre], ticks: [{ cotes: [k1], n: 1 }, { cotes: [k2], n: 2 }] } },
    ];
    const sansErreur = Math.random() < 1 / 3;
    const erreur = sansErreur ? { code: 'aucune', codes: bon } : randomChoice(erreurs);
    const codes = erreur.codes;
    const svg = svgFigure(pose, L, codes);

    // Les faits, relus sur `codes` (jamais sur le nom de l'erreur)
    const carres = codes.carres || [];
    const ticks = codes.ticks || [];
    const nDe = (k) => { const g = ticks.find((t) => t.cotes.includes(k)); return g ? g.n : 0; };
    const indiqueEgal = (a, b) => nDe(a) > 0 && nDe(a) === nDe(b);
    const carreEn = carres.includes(iCarre);
    const egaliteAnnoncee = indiqueEgal(k1, k2);
    // un codage que l'énoncé ne donne pas : un carré ailleurs qu'en S, ou une égalité codée hors de la paire annoncée
    let enTrop = carres.some((i) => i !== iCarre);
    for (let a = 0; a < n && !enTrop; a++) for (let b = a + 1; b < n; b++) {
        if (indiqueEgal(a, b) && !((a === k1 && b === k2) || (a === k2 && b === k1))) { enTrop = true; break; }
    }
    const correct = carreEn && egaliteAnnoncee && !enTrop && carres.length === 1;
    garde(correct === sansErreur, `cohérence erreur ↔ faits (${erreur.code})`);

    const S = L[iCarre];
    const eg = egal(L, k1, k2, pivot);
    const enonce = forme === 'triangle_rect_iso'
        ? `Le triangle ${nomFigure(L)} est <b>rectangle en ${S}</b> et <b>${eg}</b>.`
        : `Le pentagone ${nomFigure(L)} a <b>un angle droit en ${S}</b> et <b>${eg}</b>.`;
    const lignes = [
        `Il y a un petit carré au sommet ${S}.`,
        `Le codage indique que ${eg}.`,
        'La figure porte un codage que l\'énoncé ne donne pas.',
        'La figure est correctement codée.',
    ];
    const bonnes = [carreEn ? 0 : 1, egaliteAnnoncee ? 0 : 1, enTrop ? 0 : 1, correct ? 0 : 1];
    const payload = makeVraiFaux(lignes, bonnes,
        `<p>${enonce} Un élève a codé la figure ci-dessous (tracée à main levée).</p>`
        + '<p><b>Vrai ou faux ?</b></p><p>{{element:0}}</p>',
        { ...opts(L_JUSTE), elements_additionnels: elImage(dataUrl(svg)) });
    payload.contenu = { ...payload.contenu, sousType: `juste_${forme}` };
    payload._meta.params = { sousType: `juste_${forme}`, erreur: erreur.code };
    return payload;
};

// ═══════════════════════════════════════════════════════════════════════════
// Carte 4 — Chaque propriété avec son codage (relier, planche de 3 figures)
// ═══════════════════════════════════════════════════════════════════════════

const genProprieteRelier = () => {
    const forme = randomChoice(['triangle_rect_iso', 'quadri_un_droit']);
    const fig = FIGURES[forme]();
    const n = fig.pts.length;
    const L = pickLettresSains(LETTRES_POOL, n);
    const pose = poserFigure(fig.pts, 30);
    const S = L[1];
    // les trois propriétés en S : angle droit seul, côtés égaux seuls, les deux
    const proprietes = [
        { texte: n === 3 ? `rectangle en ${S}` : `un angle droit en ${S}`, codes: { carres: [1] } },
        { texte: `${egal(L, 0, 1, 1)}`, codes: { ticks: [{ cotes: [0, 1], n: 1 }] } },
        { texte: n === 3 ? `rectangle en ${S} et ${egal(L, 0, 1, 1)}` : `un angle droit en ${S} et ${egal(L, 0, 1, 1)}`, codes: { carres: [1], ticks: [{ cotes: [0, 1], n: 1 }] } },
    ];
    const ordre = shuffle([0, 1, 2]);   // figure k+1 porte la propriété ordre[k]
    const svg = svgPlanche(pose, L, ordre.map((i) => proprietes[i].codes));
    const pairs = ordre.map((i, k) => ({ source: proprietes[i].texte, target: `Figure ${k + 1}` }));
    const payload = makeRelier(pairs,
        `<p>${NOM_POLY[n]} ${nomFigure(L)} est tracé trois fois à main levée, avec trois codages différents.</p>`
        + '<p><b>Relie chaque propriété à la figure qui la code.</b></p><p>{{element:0}}</p>',
        { ...opts(L_RELIER), elements_additionnels: elImage(dataUrl(svg)) });
    payload.contenu = { ...payload.contenu, sousType: `relier_${forme}` };
    payload._meta.params = { sousType: `relier_${forme}`, ordre };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'DEUX codages (petit carré, traits) ; arcs exclus ; sens PRODUCTION (1015 fait la lecture)',
        'figures À MAIN LEVÉE volontairement inexactes : angle annoncé droit tracé à 70-78° ou 102-110°, côtés annoncés égaux inégaux de 15-20 % ; le dessin embarque un leurre visuel (autre sommet à ±5° du droit, autre paire égale à 3 %) pour matérialiser code_ce_qu_il_voit',
        'QCM d\'images : quatre fois la MÊME pose, seuls les codages diffèrent ; une famille d\'erreur par distracteur',
        'crochets [AB] pour les côtés, (AB) pour les droites, angle ABC en KaTeX ; notations employées, jamais interrogées',
        'lettres saines (pickLettresSains, pool ABCDEFGHKLMNPRST), lettres du coup de pouce (U, V, W) hors pool',
    ],
    coder_qcm_images: {
        fixes: {
            code_ce_qu_il_voit: 'le sommet qui paraît droit / la paire qui paraît égale (quand le dessin en offre un ; sinon autre_sommet / autre_paire)',
            confusion_ticks: 'des traits sur les côtés de l\'angle droit à la place du carré',
            confusion_carre: 'un carré au sommet des côtés égaux à la place des traits',
            codage_incomplet: 'un seul des deux codages, un seul côté barré, un seul angle droit sur quatre',
            codage_en_trop: 'une égalité ou un angle droit codé que l\'énoncé ne donne pas',
            traits_differents: 'un trait sur l\'un, deux sur l\'autre',
            tout_pareil: 'le rectangle codé comme un carré (un trait partout)',
            autre_paire: 'des côtés voisins codés égaux',
        },
    },
    placer_qcm_unique: {
        fixes: {
            code_ce_qu_il_voit: 'le sommet qui paraît droit / la paire qui paraît égale sur le dessin',
            autre_sommet: 'un autre sommet',
            autre_paire: 'une autre paire',
            refus_du_codage: '« on ne peut pas le savoir » — faux : l\'énoncé ou les mesures tranchent',
        },
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    coder_qcm_images: genCoderQcm,
    placer_qcm_unique: genPlacerQcm,
    juste_tableau_vf: genJusteVf,
    propriete_relier: genProprieteRelier,
};

const FORMAT_LABELS = {
    coder_qcm_images: 'QCM d\'images',
    placer_qcm_unique: 'QCM',
    juste_tableau_vf: 'Vrai / Faux',
    propriete_relier: 'Relier',
};

export const CATEGORIES = [
    { id: 'coder', label: L_CODER, formatsList: [{ key: 'coder_qcm_images', label: FORMAT_LABELS.coder_qcm_images }] },
    { id: 'placer', label: L_PLACER, formatsList: [{ key: 'placer_qcm_unique', label: FORMAT_LABELS.placer_qcm_unique }] },
    { id: 'juste', label: L_JUSTE, formatsList: [{ key: 'juste_tableau_vf', label: FORMAT_LABELS.juste_tableau_vf }] },
    { id: 'relier', label: L_RELIER, formatsList: [{ key: 'propriete_relier', label: FORMAT_LABELS.propriete_relier }] },
];

export const _internals = { FIGURES, poserFigure, svgFigure, svgPlanche, angleEn, cote, genCoderQcm, genPlacerQcm, genJusteVf, genProprieteRelier };

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
